import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import styles from './fields.module.css';

export interface MatrixFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const MatrixField: React.FC<MatrixFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = errors[element.name];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows: any[] = Array.isArray(element.rows) ? element.rows : [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: any[] = Array.isArray(element.columns) ? element.columns : [];

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={{}}
        render={({ field }) => {
          const values: Record<string, string> = field.value || {};

          const handleChange = (rowValue: string, colValue: string) => {
            field.onChange({
              ...values,
              [rowValue]: colValue,
            });
          };

          return (
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.matrixTable}>
                <thead>
                  <tr>
                    <th aria-label="Linha" />
                    {columns.map((col: any, index: number) => {
                      const colText =
                        typeof col === 'object'
                          ? col.text || col.title || col.value
                          : col;
                      return <th key={index}>{colText}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row: any, rowIndex: number) => {
                    const rowValue = typeof row === 'object' ? row.value : row;
                    const rowText = typeof row === 'object' ? row.text : row;
                    return (
                      <tr key={rowIndex}>
                        <td>{rowText}</td>
                        {columns.map((col: any, colIndex: number) => {
                          const colValue =
                            typeof col === 'object'
                              ? col.value || col.name
                              : col;
                          const isSelected = values[rowValue] === colValue;
                          return (
                            <td key={colIndex}>
                              <input
                                type="radio"
                                name={`${element.name}_${rowValue}`}
                                checked={isSelected}
                                onChange={() =>
                                  handleChange(rowValue, colValue)
                                }
                                disabled={element.readOnly}
                                aria-label={`${rowText} - ${colValue}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        }}
      />
      {error && (
        <p className={styles.errorMessage}>{error.message as string}</p>
      )}
    </div>
  );
};

export default MatrixField;
